import { inject } from '@angular/core';
import { HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { Observable, from, EMPTY } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { jwtDecode } from 'jwt-decode';
import { EntraAccountIdService } from '../auth/entra-account-id.service';

/**
 * JWT payload interface for Azure AD tokens
 */
interface AzureAdTokenPayload {
  oid?: string; // Object ID (Entra User ID)
  sub?: string;
  tid?: string;
  aud?: string;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

/**
 * Extended MSAL instance type to access internal initialization state
 */
interface MsalInstanceWithController {
  instance?: {
    controller?: {
      initialized?: boolean;
    };
    [key: string]: unknown;
  };
}

export function MsalTokenInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const msalService = inject(MsalService);
  const entraAccountIdService = inject(EntraAccountIdService);

  // Type-safe check for MSAL initialization
  const msalWithController = msalService as unknown as MsalInstanceWithController;
  if (!msalService.instance || msalWithController.instance?.controller?.initialized !== true) {
    // Optionally log or handle this case
    return next(req);
  }
  const account = msalService.instance.getActiveAccount() || msalService.instance.getAllAccounts()[0];
  if (!account) {
    // No user is signed in, proceed without token
    return next(req);
  }

  // You can get the clientId from the MsalService instance like this:
  const scopes = [`api://${msalService.instance.getConfiguration().auth.clientId}/access_as_user`];

  return from(
    msalService.instance.acquireTokenSilent({
      account,
      scopes,
    })
  ).pipe(
    switchMap(result => {
      const token = result.accessToken;

      // Decode the JWT and extract the EntraUserId (ObjectId)
      try {
        const decoded = jwtDecode<AzureAdTokenPayload>(token);
        const entraUserId = decoded.oid;
        if (entraUserId) {
          entraAccountIdService.setEntraUserId(entraUserId);
        }
      } catch (e) {
        console.log(e);
      }

      const authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
      return next(authReq);
    }),
    catchError((error) => {
      if (error.errorCode === 'interaction_required') {
        console.error("interaction_required")
        /******************************************
         * MG 9/2/2025
         * this is causing an endless loop and should be revisited
         */

        msalService.loginRedirect({ scopes });
        return EMPTY; // <-- Stop the chain, do not retry the request
      }
      throw error;
    })
  );
}
